import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import MediaListContainer from './MediaListContainer'
import PortalModal from 'components/Shared/PortalModal'
import MediaGroupPreviewModal from './element/PreviewModal/MediaGroupPreviewModal'
import SingleMediaPreview from './element/PreviewModal/SingleMediaPreview'
import { buildLiveGroupListItem, buildMediaListItems } from '../../buildMediaListItems'
import { useTranslation } from 'react-i18next'
import MediaListCard from './element/MediaListCard/MediaListCard'
import ErrorMessage from 'components/Shared/ErrorMessage'
import InfoMessage from 'components/Shared/InfoMessage'

export default function MediaListV2({
  media,
  selectiveMode,
  selectedSubGroupId = null,
  setSelectedSubGroupId,
  isCoCreateReady = false,
  readonly = false,
  // onRemoveOriginal,
  onRemoveSubGroup,
  // onReplaceOriginal,
  onAddToCoCreateSlot,
  onAddLibraryToCoCreateSlot,
  onReplaceLibraryToCoCreateSlot,
  onReplaceInCoCreateSlot,
  onRemoveFromCoCreateSlot,
  isCampaignRunning = false,
  requiredSlotRatios,
  onViewGeneratedVariants,
  isGoogleOnly = false,
  isGoogleSelected = false,
  bothPlatformsSelected = false,
  googleExactRatios = [],
  hasGoogleExactRatios = false,
  showValidationErrors = false,
  missingGoogleExactRatios = [],
  slotError = null,
  setSlotError,
}) {
  const [expandedPreview, setExpandedPreview] = useState(null)
  const { t } = useTranslation()

  const listItems = useMemo(() => buildMediaListItems(media), [media])

  const isYoutubePreview = Boolean(expandedPreview?.anchor?.youtube_video_id)
  const isGroupPreview = Boolean(expandedPreview?.subGroupId)
  const liveGroup = useMemo(() => {
    if (!isGroupPreview) {
      return null
    }

    return buildLiveGroupListItem(media, expandedPreview)
  }, [isGroupPreview, media, expandedPreview])

  useEffect(() => {
    if (isGroupPreview && expandedPreview && !liveGroup) {
      setExpandedPreview(null)
    }
  }, [isGroupPreview, expandedPreview, liveGroup])

  const modalTitle = isYoutubePreview ? t('Recommendations.campaign.setup.media.previewYoutube') : t('Recommendations.campaign.setup.media.previewVariants')

  const originalMediaForGroup = useMemo(() => {
    if (!expandedPreview?.originalId) {
      return null
    }

    return media.find((item) => item.id === expandedPreview.originalId) ?? null
  }, [expandedPreview?.originalId, media])

  if (!listItems.length) {
    return null
  }

  const canRemove = !readonly && !isCampaignRunning

  return (
    <>
      {slotError && (
        <ErrorMessage style={{ justifyContent: 'start' }} variant="light" classNames="mt-0">{slotError}</ErrorMessage>
      )}

      <MediaListContainer>
        {listItems.map((listItem) => {
          const anchor = listItem.anchor
          if (!anchor) {
            return null
          }

          const isVideoOriginal = anchor?.type?.startsWith('video')
          const isYoutubeOriginal = Boolean(anchor?.youtube_video_id)
          const groupHasHeadline = listItem.images.some((image) => image.headline != null)
          const isSelected = String(selectedSubGroupId) === String(listItem.subGroupId)
          const disableSelection = selectiveMode && (isVideoOriginal || groupHasHeadline)

          const handleRemoveSubGroup = canRemove
            ? () => {
              setSlotError(null)
              onRemoveSubGroup?.(listItem.subGroupId)
            }
            : undefined
          const handleRemoveFailedMedia = canRemove
            ? () => {
              setSlotError(null)
              onRemoveFromCoCreateSlot?.(anchor.id)
            }
            : undefined

          const originalMediaForCard = listItem.originalId
            ? media.find((item) => item.id === listItem.originalId) ?? null
            : null

          return (
            <MediaListCard
              key={listItem.key}
              item={anchor}
              selectiveMode={selectiveMode}
              disableSelection={disableSelection}
              isSelected={isSelected}
              onSelect={() => setSelectedSubGroupId?.(listItem.subGroupId)}
              isReady={isCoCreateReady && Boolean(anchor.generating)}
              showLabel={true}
              mediaLabel={{
                variant: isYoutubeOriginal
                  ? 'youtube'
                  : isVideoOriginal
                    ? 'video'
                    : (anchor.is_generated || groupHasHeadline) ? 'generated' : 'uploaded',
                headline: listItem.headline,
                bothSelected: bothPlatformsSelected,
              }}
              subGroupItems={listItem.images}
              subGroupId={listItem.subGroupId}
              originalId={listItem.originalId}
              originalMedia={originalMediaForCard}
              isCampaignRunning={isCampaignRunning}
              readonly={readonly}
              onRemove={handleRemoveFailedMedia}
              onRemoveSubGroup={handleRemoveSubGroup}
              onPreviewClick={() => setExpandedPreview(listItem)}
              onViewVariants={onViewGeneratedVariants}
              onAddToSlot={onAddToCoCreateSlot}
              onAddLibraryToSlot={onAddLibraryToCoCreateSlot}
              onReplaceLibraryToSlot={onReplaceLibraryToCoCreateSlot}
              onReplaceInSlot={onReplaceInCoCreateSlot}
              onRemoveFromSlot={onRemoveFromCoCreateSlot}
              setSlotError={setSlotError}
              requiredSlotRatios={requiredSlotRatios}
              isGoogleOnly={isGoogleOnly}
              isGoogleSelected={isGoogleSelected}
              googleExactRatios={googleExactRatios}
              showValidationErrors={showValidationErrors}
              missingGoogleExactRatios={missingGoogleExactRatios}
            />
          )
        })}
      </MediaListContainer>


      {isGoogleSelected && (
        <InfoMessage
          variant={hasGoogleExactRatios ? 'success' : (showValidationErrors ? 'warning' : 'info')}
          className="mt-0 mb-3"
          text={t(
            hasGoogleExactRatios
              ? 'Recommendations.campaign.setup.media.googleExactRatiosMet'
              : 'Recommendations.campaign.setup.media.errors.googleExactRatiosRequired',
          )}
        />
      )}

      <PortalModal
        isOpen={!!expandedPreview}
        className={`full-height ${isYoutubePreview ? 'single-media-preview-portal-modal' : 'media-group-preview-portal-modal'}`}
        onClickOutside={() => setExpandedPreview(null)}
        title={modalTitle}
      >
        {isYoutubePreview ? (
          <SingleMediaPreview file={expandedPreview.anchor} />
        ) : isGroupPreview && liveGroup ? (
          <MediaGroupPreviewModal
            group={liveGroup}
            originalMedia={originalMediaForGroup}
            isCampaignRunning={isCampaignRunning}
            readonly={readonly}
            onAddToSlot={onAddToCoCreateSlot}
            onAddLibraryToSlot={onAddLibraryToCoCreateSlot}
            onReplaceLibraryToSlot={onReplaceLibraryToCoCreateSlot}
            onReplaceInSlot={onReplaceInCoCreateSlot}
            onRemoveFromSlot={onRemoveFromCoCreateSlot}
            requiredSlotRatios={requiredSlotRatios}
            isGoogleOnly={isGoogleOnly}
            isGoogleSelected={isGoogleSelected}
            bothPlatformsSelected={bothPlatformsSelected}
            googleExactRatios={googleExactRatios}
            slotError={slotError}
            setSlotError={setSlotError}
          />
        ) : null}
      </PortalModal>
    </>
  )
}

MediaListV2.propTypes = {
  media: PropTypes.array,
  selectiveMode: PropTypes.bool,
  selectedSubGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedSubGroupId: PropTypes.func,
  isCoCreateReady: PropTypes.bool,
  readonly: PropTypes.bool,
  isCampaignRunning: PropTypes.bool,
  onRemoveOriginal: PropTypes.func,
  onRemoveSubGroup: PropTypes.func,
  onAddToCoCreateSlot: PropTypes.func,
  onAddLibraryToCoCreateSlot: PropTypes.func,
  onReplaceLibraryToCoCreateSlot: PropTypes.func,
  onReplaceInCoCreateSlot: PropTypes.func,
  onRemoveFromCoCreateSlot: PropTypes.func,
  requiredSlotRatios: PropTypes.arrayOf(PropTypes.string),
  onViewGeneratedVariants: PropTypes.func,
  isGoogleOnly: PropTypes.bool,
  isGoogleSelected: PropTypes.bool,
  bothPlatformsSelected: PropTypes.bool,
  googleExactRatios: PropTypes.arrayOf(PropTypes.string),
  hasGoogleExactRatios: PropTypes.bool,
  showValidationErrors: PropTypes.bool,
  missingGoogleExactRatios: PropTypes.arrayOf(PropTypes.string),
  slotError: PropTypes.string,
  setSlotError: PropTypes.func,
}
